import { useReducer, useMemo } from 'react'
import { CALCULATION_TYPES, DEFAULT_CONFIG, GROUPING_PRESETS, createColumnInstance } from '../lib/constants'

const actions = {
  SET_GROUPINGS: 'SET_GROUPINGS',
  ADD_GROUPING: 'ADD_GROUPING',
  REMOVE_GROUPING: 'REMOVE_GROUPING',
  REORDER_GROUPINGS: 'REORDER_GROUPINGS',
  ADD_COLUMN: 'ADD_COLUMN',
  REMOVE_COLUMN: 'REMOVE_COLUMN',
  SET_COLUMNS: 'SET_COLUMNS',
  SET_QUICK_DATE: 'SET_QUICK_DATE',
  SET_DATE_RANGE: 'SET_DATE_RANGE',
  SET_FETCH_ALL_LEVELS: 'SET_FETCH_ALL_LEVELS',
  SET_GROUPING_PRESET: 'SET_GROUPING_PRESET',
  RESET: 'RESET'
}

function reducer (state, action) {
  switch (action.type) {
    case actions.SET_GROUPINGS:
      return { ...state, groupings: action.payload, groupingPreset: 'custom' }
    case actions.ADD_GROUPING:
      if (state.groupings.includes(action.payload)) return state
      return { ...state, groupings: [...state.groupings, action.payload], groupingPreset: 'custom' }
    case actions.REMOVE_GROUPING:
      return { ...state, groupings: state.groupings.filter(g => g !== action.payload), groupingPreset: 'custom' }
    case actions.REORDER_GROUPINGS:
      return { ...state, groupings: action.payload, groupingPreset: 'custom' }
    case actions.SET_GROUPING_PRESET: {
      const preset = GROUPING_PRESETS.find(p => p.value === action.payload)
      if (!preset || preset.value === 'custom') {
        return { ...state, groupingPreset: 'custom' }
      }
      return { ...state, groupingPreset: preset.value, groupings: preset.groupings }
    }
    case actions.ADD_COLUMN: {
      const instance = action.payload
      if (!instance) return state
      return { ...state, columns: [...state.columns, instance] }
    }
    case actions.REMOVE_COLUMN:
      return { ...state, columns: state.columns.filter(c => c.id !== action.payload) }
    case actions.SET_COLUMNS:
      return { ...state, columns: action.payload }
    case actions.SET_QUICK_DATE:
      return { ...state, dateMode: 'quickDate', quickDate: action.payload, startDate: null, endDate: null }
    case actions.SET_DATE_RANGE:
      return { ...state, dateMode: 'dateRange', quickDate: null, startDate: action.payload.startDate, endDate: action.payload.endDate }
    case actions.SET_FETCH_ALL_LEVELS:
      return { ...state, fetchAllLevels: action.payload }
    case actions.RESET:
      return { ...DEFAULT_CONFIG }
    default:
      return state
  }
}

export function useExplorerConfig () {
  const [config, dispatch] = useReducer(reducer, DEFAULT_CONFIG)

  const columnDefinitions = useMemo(() => {
    return config.columns.flatMap(col => {
      const typeDef = CALCULATION_TYPES[col.type]
      return typeDef ? typeDef.columns(col.id, col.options) : []
    })
  }, [config.columns])

  return {
    config,
    columnDefinitions,
    setGroupings: (groupings) => dispatch({ type: actions.SET_GROUPINGS, payload: groupings }),
    addGrouping: (grouping) => dispatch({ type: actions.ADD_GROUPING, payload: grouping }),
    removeGrouping: (grouping) => dispatch({ type: actions.REMOVE_GROUPING, payload: grouping }),
    reorderGroupings: (groupings) => dispatch({ type: actions.REORDER_GROUPINGS, payload: groupings }),
    setGroupingPreset: (preset) => dispatch({ type: actions.SET_GROUPING_PRESET, payload: preset }),
    addColumn: (type, optionOverrides) => dispatch({ type: actions.ADD_COLUMN, payload: createColumnInstance(type, optionOverrides) }),
    removeColumn: (id) => dispatch({ type: actions.REMOVE_COLUMN, payload: id }),
    setColumns: (columns) => dispatch({ type: actions.SET_COLUMNS, payload: columns }),
    setQuickDate: (quickDate) => dispatch({ type: actions.SET_QUICK_DATE, payload: quickDate }),
    setDateRange: (startDate, endDate) => dispatch({ type: actions.SET_DATE_RANGE, payload: { startDate, endDate } }),
    setFetchAllLevels: (value) => dispatch({ type: actions.SET_FETCH_ALL_LEVELS, payload: value }),
    reset: () => dispatch({ type: actions.RESET })
  }
}
